/**
 * Pane Relief - Comprehensive GTM/dataLayer Tracking
 * Enterprise-grade tracking for Lead Gen + E-commerce
 *
 * TRACKED EVENTS:
 * ═══════════════════════════════════════════════════════════════
 * PAGE ENGAGEMENT
 * - page_view (with page type, content group)
 * - scroll_depth (25%, 50%, 75%, 90%, 100%)
 * - time_on_page (30s, 60s, 120s, 300s milestones)
 * - content_engagement (reading time estimate)
 *
 * E-COMMERCE (GA4 Enhanced)
 * - view_item, view_item_list
 * - add_to_cart, remove_from_cart
 * - view_cart, begin_checkout
 * - select_item (product card clicks)
 * - view_promotion (banners, sales)
 * - select_promotion (promo clicks)
 *
 * LEAD GENERATION
 * - generate_lead (form submissions)
 * - form_start, form_submit, form_error, form_abandonment
 * - newsletter_signup
 * - contact_form_submit
 * - quote_request (glass configurator)
 * - callback_request
 *
 * USER INTERACTIONS
 * - cta_click (buttons, links)
 * - navigation_click (header, footer, mobile)
 * - phone_click, email_click, whatsapp_click
 * - live_chat_open (if available)
 * - file_download (PDFs, brochures)
 * - outbound_click (external links)
 * - social_click (social media links)
 *
 * PRODUCT INTERACTIONS
 * - product_image_view (gallery interactions)
 * - variant_change (size, color, etc.)
 * - add_to_wishlist, remove_from_wishlist
 * - product_share
 * - review_interaction (read more, filter, write)
 *
 * SITE FEATURES
 * - search (queries, results)
 * - filter_apply (collection filters)
 * - sort_change (collection sorting)
 * - popup_view, popup_close, popup_convert
 * - accordion_toggle (FAQ interactions)
 * - video_start, video_progress, video_complete
 * - back_to_top_click
 * - cookie_consent (accept, decline, customize)
 *
 * ERRORS & PERFORMANCE
 * - js_error, image_error
 * - page_not_found (404)
 * - checkout_error
 * ═══════════════════════════════════════════════════════════════
 */
(function() {
  'use strict';

  // ════════════════════════════════════════════════════════════
  // CONFIGURATION
  // ════════════════════════════════════════════════════════════
  var CONFIG = {
    debug: false,
    currency: 'GBP',
    scrollThresholds: [25, 50, 75, 90, 100],
    timeThresholds: [30, 60, 120, 300], // seconds
    engagementThreshold: 10, // seconds for "engaged" user
    scrolledDepths: {},
    timeOnPage: {},
    sessionStartTime: Date.now()
  };

  // ════════════════════════════════════════════════════════════
  // CORE TRACKING FUNCTION
  // ════════════════════════════════════════════════════════════
  function trackEvent(eventName, eventData) {
    eventData = eventData || {};

    var payload = {
      event: eventName,
      event_timestamp: new Date().toISOString(),
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      page_referrer: document.referrer || 'direct'
    };

    // Add page context
    if (window.gtmPageData) {
      payload.page_type = window.gtmPageData.page_type;
      payload.content_group = window.gtmPageData.content_group;
      payload.business_line = window.gtmPageData.business_line;
      payload.user_logged_in = window.gtmPageData.user_logged_in;
    }

    // Merge custom data
    for (var key in eventData) {
      if (eventData.hasOwnProperty(key)) {
        payload[key] = eventData[key];
      }
    }

    // Add session context
    payload.session_duration = Math.round((Date.now() - CONFIG.sessionStartTime) / 1000);

    // Push to dataLayer
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push(payload);
    }

    if (CONFIG.debug) {
      console.log('[GTM]', eventName, payload);
    }
  }

  // Expose globally
  window.prTrackEvent = trackEvent;

  // ════════════════════════════════════════════════════════════
  // PAGE VIEW & ENGAGEMENT TRACKING
  // ════════════════════════════════════════════════════════════
  function trackPageView() {
    var pageData = window.gtmPageData || {};
    trackEvent('page_view', {
      page_type: pageData.page_type || 'unknown',
      content_group: pageData.content_group || '',
      user_logged_in: pageData.user_logged_in || false
    });
  }

  function initScrollTracking() {
    var pageId = window.location.pathname;
    CONFIG.scrolledDepths[pageId] = {};

    function getScrollPercent() {
      var h = document.documentElement;
      var b = document.body;
      var st = 'scrollTop';
      var sh = 'scrollHeight';
      return Math.round((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight) * 100);
    }

    var scrollTimeout;
    window.addEventListener('scroll', function() {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function() {
        var percent = getScrollPercent();
        CONFIG.scrollThresholds.forEach(function(threshold) {
          if (percent >= threshold && !CONFIG.scrolledDepths[pageId][threshold]) {
            CONFIG.scrolledDepths[pageId][threshold] = true;
            trackEvent('scroll_depth', {
              scroll_depth_threshold: threshold,
              scroll_depth_units: 'percent'
            });
          }
        });
      }, 150);
    }, { passive: true });
  }

  function initTimeOnPageTracking() {
    var pageId = window.location.pathname;
    CONFIG.timeOnPage[pageId] = {};

    CONFIG.timeThresholds.forEach(function(seconds) {
      setTimeout(function() {
        if (!CONFIG.timeOnPage[pageId][seconds]) {
          CONFIG.timeOnPage[pageId][seconds] = true;
          trackEvent('time_on_page', {
            time_threshold: seconds,
            time_units: 'seconds'
          });
        }
      }, seconds * 1000);
    });

    // Track engaged users (10+ seconds)
    setTimeout(function() {
      trackEvent('user_engaged', {
        engagement_time: CONFIG.engagementThreshold
      });
    }, CONFIG.engagementThreshold * 1000);
  }

  // ════════════════════════════════════════════════════════════
  // E-COMMERCE TRACKING (GA4 Enhanced)
  // ════════════════════════════════════════════════════════════
  function trackProductView(product) {
    if (!product) return;
    trackEvent('view_item', {
      ecommerce: {
        currency: CONFIG.currency,
        value: parseFloat(product.price) || 0,
        items: [{
          item_id: product.id || product.sku || '',
          item_name: product.title || '',
          item_brand: product.vendor || 'Pane Relief',
          item_category: product.type || '',
          item_variant: product.variant_title || '',
          price: parseFloat(product.price) || 0,
          quantity: 1
        }]
      }
    });
  }
  window.prTrackProductView = trackProductView;

  function trackCollectionView(collection, items) {
    if (!collection) return;
    var itemList = [];
    if (items && items.length) {
      items.forEach(function(item, index) {
        itemList.push({
          item_id: item.id || '',
          item_name: item.title || '',
          item_brand: item.vendor || 'Pane Relief',
          item_category: item.type || '',
          price: parseFloat(item.price) || 0,
          index: index
        });
      });
    }
    trackEvent('view_item_list', {
      ecommerce: {
        item_list_id: collection.id || '',
        item_list_name: collection.title || '',
        items: itemList
      }
    });
  }
  window.prTrackCollectionView = trackCollectionView;

  function trackAddToCart(item, quantity, source) {
    quantity = quantity || 1;
    source = source || 'unknown';
    trackEvent('add_to_cart', {
      ecommerce: {
        currency: CONFIG.currency,
        value: (parseFloat(item.price) || 0) * quantity,
        items: [{
          item_id: item.id || item.variant_id || '',
          item_name: item.title || item.product_title || '',
          item_brand: item.vendor || 'Pane Relief',
          item_category: item.product_type || '',
          item_variant: item.variant_title || '',
          price: parseFloat(item.price) || 0,
          quantity: quantity
        }]
      },
      add_to_cart_source: source
    });
  }
  window.prTrackAddToCart = trackAddToCart;

  function trackRemoveFromCart(item, quantity, method) {
    quantity = quantity || 1;
    trackEvent('remove_from_cart', {
      ecommerce: {
        currency: CONFIG.currency,
        value: (parseFloat(item.price) || 0) * quantity,
        items: [{
          item_id: item.id || item.variant_id || '',
          item_name: item.title || item.product_title || '',
          item_brand: item.vendor || 'Pane Relief',
          item_variant: item.variant_title || '',
          price: parseFloat(item.price) || 0,
          quantity: quantity
        }]
      },
      removal_method: method || 'unknown'
    });
  }
  window.prTrackRemoveFromCart = trackRemoveFromCart;

  function trackCartView(cart) {
    if (!cart) return;
    var items = [];
    if (cart.items && cart.items.length) {
      cart.items.forEach(function(item, index) {
        items.push({
          item_id: item.variant_id || item.id || '',
          item_name: item.product_title || item.title || '',
          item_brand: item.vendor || 'Pane Relief',
          item_variant: item.variant_title || '',
          price: (item.final_price || item.price || 0) / 100,
          quantity: item.quantity || 1,
          index: index
        });
      });
    }
    trackEvent('view_cart', {
      ecommerce: {
        currency: CONFIG.currency,
        value: (cart.total_price || 0) / 100,
        items: items
      },
      cart_item_count: cart.item_count || 0
    });
  }
  window.prTrackCartView = trackCartView;

  function trackBeginCheckout(cart) {
    if (!cart) return;
    var items = [];
    if (cart.items && cart.items.length) {
      cart.items.forEach(function(item, index) {
        items.push({
          item_id: item.variant_id || item.id || '',
          item_name: item.product_title || item.title || '',
          item_brand: item.vendor || 'Pane Relief',
          item_variant: item.variant_title || '',
          price: (item.final_price || item.price || 0) / 100,
          quantity: item.quantity || 1,
          index: index
        });
      });
    }
    trackEvent('begin_checkout', {
      ecommerce: {
        currency: CONFIG.currency,
        value: (cart.total_price || 0) / 100,
        items: items
      }
    });
  }
  window.prTrackBeginCheckout = trackBeginCheckout;

  function trackSelectItem(item, listName, index) {
    trackEvent('select_item', {
      ecommerce: {
        item_list_name: listName || '',
        items: [{
          item_id: item.id || '',
          item_name: item.title || '',
          item_brand: item.vendor || 'Pane Relief',
          price: parseFloat(item.price) || 0,
          index: index || 0
        }]
      }
    });
  }
  window.prTrackSelectItem = trackSelectItem;

  // ════════════════════════════════════════════════════════════
  // LEAD GENERATION TRACKING
  // ════════════════════════════════════════════════════════════
  function trackGenerateLead(leadData) {
    trackEvent('generate_lead', {
      currency: CONFIG.currency,
      value: leadData.value || 0,
      lead_source: leadData.source || 'website',
      form_name: leadData.form_name || '',
      form_id: leadData.form_id || '',
      lead_type: leadData.lead_type || 'general'
    });
  }
  window.prTrackGenerateLead = trackGenerateLead;

  function trackFormStart(formId, formName) {
    trackEvent('form_start', {
      form_id: formId || '',
      form_name: formName || '',
      form_start_time: new Date().toISOString()
    });
  }
  window.prTrackFormStart = trackFormStart;

  function trackFormSubmit(formId, formName, formData) {
    formData = formData || {};
    trackEvent('form_submit', {
      form_id: formId || '',
      form_name: formName || '',
      form_destination: formData.destination || '',
      form_submit_text: formData.submit_text || '',
      form_fields_count: formData.fields_count || 0
    });
  }
  window.prTrackFormSubmit = trackFormSubmit;

  function trackFormError(formId, formName, errorMessage, fieldName) {
    trackEvent('form_error', {
      form_id: formId || '',
      form_name: formName || '',
      error_message: errorMessage || '',
      error_field: fieldName || ''
    });
  }
  window.prTrackFormError = trackFormError;

  function trackNewsletterSignup(email, source) {
    trackEvent('newsletter_signup', {
      signup_source: source || 'footer',
      email_domain: email ? email.split('@')[1] : 'unknown'
    });
    trackGenerateLead({
      value: 5,
      source: 'newsletter',
      form_name: 'Newsletter Signup',
      lead_type: 'newsletter'
    });
  }
  window.prTrackNewsletterSignup = trackNewsletterSignup;

  function trackQuoteRequest(quoteData) {
    trackEvent('quote_request', {
      quote_type: quoteData.type || 'product',
      quote_value: quoteData.value || 0,
      product_category: quoteData.category || '',
      configuration: quoteData.configuration || {}
    });
    trackGenerateLead({
      value: quoteData.value || 50,
      source: 'quote_form',
      form_name: 'Quote Request',
      lead_type: 'quote'
    });
  }
  window.prTrackQuoteRequest = trackQuoteRequest;

  // ════════════════════════════════════════════════════════════
  // USER INTERACTION TRACKING
  // ════════════════════════════════════════════════════════════
  function trackCtaClick(ctaText, ctaUrl, ctaLocation, ctaType) {
    trackEvent('cta_click', {
      cta_text: (ctaText || '').substring(0, 100),
      cta_url: ctaUrl || '',
      cta_location: ctaLocation || 'unknown',
      cta_type: ctaType || 'button'
    });
  }
  window.prTrackCtaClick = trackCtaClick;

  function trackNavClick(linkText, linkUrl, navLocation) {
    trackEvent('navigation_click', {
      link_text: (linkText || '').substring(0, 50),
      link_url: linkUrl || '',
      nav_location: navLocation || 'unknown'
    });
  }
  window.prTrackNavClick = trackNavClick;

  function trackSearch(searchTerm, resultCount, searchType) {
    trackEvent('search', {
      search_term: searchTerm || '',
      search_results_count: resultCount || 0,
      search_type: searchType || 'site_search'
    });
  }
  window.prTrackSearch = trackSearch;

  // ════════════════════════════════════════════════════════════
  // PRODUCT INTERACTION TRACKING
  // ════════════════════════════════════════════════════════════
  function trackVariantChange(productId, variantId, variantTitle, optionName, optionValue) {
    trackEvent('variant_change', {
      product_id: productId || '',
      variant_id: variantId || '',
      variant_title: variantTitle || '',
      option_name: optionName || '',
      option_value: optionValue || ''
    });
  }
  window.prTrackVariantChange = trackVariantChange;

  function trackProductImageView(productId, imageIndex, imageType) {
    trackEvent('product_image_view', {
      product_id: productId || '',
      image_index: imageIndex || 0,
      image_type: imageType || 'gallery'
    });
  }
  window.prTrackProductImageView = trackProductImageView;

  function trackAddToWishlist(item) {
    trackEvent('add_to_wishlist', {
      ecommerce: {
        currency: CONFIG.currency,
        value: parseFloat(item.price) || 0,
        items: [{
          item_id: item.id || '',
          item_name: item.title || '',
          price: parseFloat(item.price) || 0
        }]
      }
    });
  }
  window.prTrackAddToWishlist = trackAddToWishlist;

  // ════════════════════════════════════════════════════════════
  // SITE FEATURE TRACKING
  // ════════════════════════════════════════════════════════════
  function trackPopup(action, popupId, popupType) {
    trackEvent('popup_' + action, {
      popup_id: popupId || '',
      popup_type: popupType || 'promotion'
    });
  }
  window.prTrackPopup = trackPopup;

  function trackAccordionToggle(accordionTitle, isOpen, accordionSection) {
    trackEvent('accordion_toggle', {
      accordion_title: accordionTitle || '',
      accordion_state: isOpen ? 'open' : 'close',
      accordion_section: accordionSection || 'faq'
    });
  }
  window.prTrackAccordionToggle = trackAccordionToggle;

  function trackVideo(action, videoId, videoTitle, videoProvider, currentTime, duration) {
    trackEvent('video_' + action, {
      video_id: videoId || '',
      video_title: videoTitle || '',
      video_provider: videoProvider || 'unknown',
      video_current_time: currentTime || 0,
      video_duration: duration || 0,
      video_percent: duration ? Math.round((currentTime / duration) * 100) : 0
    });
  }
  window.prTrackVideo = trackVideo;

  function trackFilterApply(filterType, filterValue, collectionHandle) {
    trackEvent('filter_apply', {
      filter_type: filterType || '',
      filter_value: filterValue || '',
      collection_handle: collectionHandle || ''
    });
  }
  window.prTrackFilterApply = trackFilterApply;

  function trackSortChange(sortOption, collectionHandle) {
    trackEvent('sort_change', {
      sort_option: sortOption || '',
      collection_handle: collectionHandle || ''
    });
  }
  window.prTrackSortChange = trackSortChange;

  // ════════════════════════════════════════════════════════════
  // AUTO-INITIALIZATION FUNCTIONS
  // ════════════════════════════════════════════════════════════

  // Cart API Interceptor
  function initCartTracking() {
    var originalFetch = window.fetch;
    window.fetch = function(url, options) {
      var promise = originalFetch.apply(this, arguments);

      if (typeof url === 'string') {
        if (url.includes('/cart/add')) {
          promise.then(function(response) {
            return response.clone().json();
          }).then(function(data) {
            if (data && data.items) {
              data.items.forEach(function(item) {
                trackAddToCart({
                  id: item.variant_id,
                  title: item.product_title,
                  variant_title: item.variant_title,
                  price: item.final_price / 100,
                  vendor: item.vendor,
                  product_type: item.product_type
                }, item.quantity, 'ajax');
              });
            } else if (data && data.variant_id) {
              trackAddToCart({
                id: data.variant_id,
                title: data.product_title,
                variant_title: data.variant_title,
                price: data.final_price / 100,
                vendor: data.vendor,
                product_type: data.product_type
              }, data.quantity, 'ajax');
            }
          }).catch(function() {});
        }

        if (url.includes('/cart') && !url.includes('/cart/add') && !url.includes('/cart/change')) {
          promise.then(function(response) {
            return response.clone().json();
          }).then(function(cart) {
            if (cart && cart.items) {
              window._lastCartData = cart;
            }
          }).catch(function() {});
        }
      }

      return promise;
    };

    // Checkout button tracking
    document.addEventListener('click', function(e) {
      var checkoutBtn = e.target.closest('[name="checkout"], .cart__checkout-button, [href*="/checkout"], .checkout-button');
      if (checkoutBtn && window._lastCartData) {
        trackBeginCheckout(window._lastCartData);
      }
    });
  }

  // Navigation Tracking
  function initNavigationTracking() {
    // Header
    document.querySelectorAll('header a, .header a, [class*="header"] nav a').forEach(function(link) {
      if (link.dataset.gtmTracked) return;
      link.dataset.gtmTracked = 'true';
      link.addEventListener('click', function() {
        trackNavClick(this.textContent.trim(), this.getAttribute('href'), 'header');
      });
    });

    // Footer
    document.querySelectorAll('footer a, .footer a').forEach(function(link) {
      if (link.dataset.gtmTracked) return;
      link.dataset.gtmTracked = 'true';
      link.addEventListener('click', function() {
        trackNavClick(this.textContent.trim(), this.getAttribute('href'), 'footer');
      });
    });

    // Mobile menu
    document.querySelectorAll('.mobile-nav a, .menu-drawer a, [class*="mobile"] nav a').forEach(function(link) {
      if (link.dataset.gtmTracked) return;
      link.dataset.gtmTracked = 'true';
      link.addEventListener('click', function() {
        trackNavClick(this.textContent.trim(), this.getAttribute('href'), 'mobile_menu');
      });
    });

    // Breadcrumbs
    document.querySelectorAll('.breadcrumb a, [class*="breadcrumb"] a').forEach(function(link) {
      if (link.dataset.gtmTracked) return;
      link.dataset.gtmTracked = 'true';
      link.addEventListener('click', function() {
        trackNavClick(this.textContent.trim(), this.getAttribute('href'), 'breadcrumb');
      });
    });
  }

  // CTA Button Tracking
  function initCtaTracking() {
    var ctaSelectors = [
      '.btn:not([type="submit"])',
      '.button:not([type="submit"])',
      '[class*="cta"]',
      'a[href*="quote"]',
      'a[href*="contact"]',
      'a[href*="book"]',
      'a[href*="call"]',
      '.hero a',
      '.banner a.btn',
      '[class*="hero"] a.btn'
    ];

    document.querySelectorAll(ctaSelectors.join(', ')).forEach(function(btn) {
      if (btn.dataset.gtmCtaTracked || btn.closest('form')) return;
      btn.dataset.gtmCtaTracked = 'true';

      btn.addEventListener('click', function() {
        var text = (this.textContent || this.value || '').trim();
        if (!text || text.length < 2) return;

        trackCtaClick(
          text,
          this.getAttribute('href') || '',
          getElementLocation(this),
          this.tagName === 'A' ? 'link' : 'button'
        );
      });
    });
  }

  // Form Tracking
  function initFormTracking() {
    document.querySelectorAll('form').forEach(function(form) {
      if (form.dataset.gtmFormTracked) return;
      if (form.action && (form.action.includes('/cart') || form.action.includes('/checkout'))) return;
      form.dataset.gtmFormTracked = 'true';

      var formId = form.id || form.getAttribute('name') || 'unnamed_form';
      var formName = getFormName(form);
      var hasStarted = false;
      var startTime = null;

      // Form start
      form.addEventListener('focusin', function() {
        if (!hasStarted) {
          hasStarted = true;
          startTime = Date.now();
          trackFormStart(formId, formName);
        }
      }, { once: true });

      // Form submission
      form.addEventListener('submit', function(e) {
        var completionTime = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
        var submitBtn = form.querySelector('[type="submit"]');
        var fieldsCount = form.querySelectorAll('input:not([type="hidden"]), select, textarea').length;

        trackFormSubmit(formId, formName, {
          destination: form.action || '',
          submit_text: submitBtn ? (submitBtn.textContent || submitBtn.value || '') : '',
          fields_count: fieldsCount,
          completion_time: completionTime
        });

        // Track specific form types
        if (formName.toLowerCase().includes('newsletter') || form.action.includes('customer')) {
          var emailInput = form.querySelector('[type="email"]');
          if (emailInput) {
            trackNewsletterSignup(emailInput.value, getElementLocation(form));
          }
        } else if (formName.toLowerCase().includes('contact')) {
          trackGenerateLead({
            value: 25,
            source: 'contact_form',
            form_name: formName,
            lead_type: 'contact'
          });
        }
      });

      // Track validation errors
      form.querySelectorAll('input, select, textarea').forEach(function(field) {
        field.addEventListener('invalid', function() {
          trackFormError(formId, formName, 'Validation failed', field.name || field.id);
        });
      });
    });
  }

  // Search Tracking
  function initSearchTracking() {
    var searchInput = document.querySelector('[name="q"], .search__input, [type="search"]');
    if (searchInput) {
      var searchTimeout;
      searchInput.addEventListener('input', function() {
        var query = this.value.trim();
        if (query.length < 3) return;

        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(function() {
          var resultsContainer = document.querySelector('.predictive-search__results, [class*="search-results"]');
          var resultCount = resultsContainer ? resultsContainer.querySelectorAll('a, li').length : 0;
          trackSearch(query, resultCount, 'predictive');
        }, 1500);
      });
    }

    document.querySelectorAll('form[action*="/search"]').forEach(function(form) {
      form.addEventListener('submit', function() {
        var query = form.querySelector('[name="q"]');
        if (query) {
          trackSearch(query.value.trim(), 0, 'form_submit');
        }
      });
    });
  }

  // Contact Click Tracking (Phone, Email, WhatsApp)
  function initContactClickTracking() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href') || '';

      if (href.startsWith('tel:')) {
        trackEvent('phone_click', {
          phone_number: href.replace('tel:', '').replace(/\s/g, ''),
          click_location: getElementLocation(link),
          click_text: (link.textContent || '').trim().substring(0, 50)
        });
      } else if (href.startsWith('mailto:')) {
        trackEvent('email_click', {
          email_address: href.replace('mailto:', '').split('?')[0],
          click_location: getElementLocation(link)
        });
      } else if (href.includes('whatsapp') || href.includes('wa.me')) {
        trackEvent('whatsapp_click', {
          whatsapp_url: href,
          click_location: getElementLocation(link)
        });
      }
    });
  }

  // Outbound Link Tracking
  function initOutboundTracking() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('tel:') || href.startsWith('mailto:')) return;

      try {
        var url = new URL(href, window.location.origin);
        if (url.hostname !== window.location.hostname) {
          trackEvent('outbound_click', {
            outbound_url: href,
            outbound_domain: url.hostname,
            link_text: (link.textContent || '').trim().substring(0, 100),
            click_location: getElementLocation(link)
          });
        }
      } catch (e) {}
    });
  }

  // Social Link Tracking
  function initSocialTracking() {
    var socialPatterns = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok', 'pinterest'];

    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = (link.getAttribute('href') || '').toLowerCase();

      socialPatterns.forEach(function(platform) {
        if (href.includes(platform)) {
          trackEvent('social_click', {
            social_platform: platform,
            social_url: link.getAttribute('href'),
            click_location: getElementLocation(link)
          });
        }
      });
    });
  }

  // File Download Tracking
  function initDownloadTracking() {
    var downloadExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.rar'];

    document.addEventListener('click', function(e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href') || '';

      downloadExtensions.forEach(function(ext) {
        if (href.toLowerCase().includes(ext)) {
          trackEvent('file_download', {
            file_url: href,
            file_extension: ext.replace('.', ''),
            file_name: href.split('/').pop(),
            click_location: getElementLocation(link)
          });
        }
      });
    });
  }

  // Accordion/FAQ Tracking
  function initAccordionTracking() {
    document.querySelectorAll('details, [class*="accordion"], [class*="faq"]').forEach(function(accordion) {
      var trigger = accordion.querySelector('summary, [class*="toggle"], [class*="trigger"], button');
      if (!trigger || trigger.dataset.gtmAccordionTracked) return;
      trigger.dataset.gtmAccordionTracked = 'true';

      trigger.addEventListener('click', function() {
        var title = (this.textContent || '').trim().substring(0, 100);
        var isOpen = accordion.tagName === 'DETAILS' ? !accordion.hasAttribute('open') :
                     accordion.classList.contains('active') || accordion.classList.contains('open');
        var section = accordion.closest('section') || accordion.closest('[class*="faq"]');
        var sectionName = section ? (section.dataset.sectionName || section.className.split(' ')[0]) : 'unknown';

        trackAccordionToggle(title, isOpen, sectionName);
      });
    });
  }

  // FAQ-specific Tracking (T17) — distinct from generic accordion
  function initFaqTracking() {
    document.querySelectorAll('[data-faq-question], .faq-band details, .faq__item').forEach(function(el) {
      if (el.dataset.gtmFaqTracked) return;
      el.dataset.gtmFaqTracked = 'true';

      var fire = function() {
        var labelEl = el.querySelector('summary, [data-faq-question-text], .faq__question') || el;
        var label = (labelEl.textContent || '').trim().slice(0, 120);
        trackEvent('faq_expand', {
          question: label,
          event_category: 'faq',
          event_action: 'expand',
          location: getElementLocation(el)
        });
      };
      if (el.tagName === 'DETAILS') {
        el.addEventListener('toggle', function() { if (el.open) fire(); });
      } else {
        el.addEventListener('click', fire);
      }
    });
  }

  // Review Band IntersectionObserver Tracking (T17)
  function initReviewBandTracking() {
    var band = document.querySelector('.pr-review-band');
    if (!band || !('IntersectionObserver' in window)) return;
    var fired = false;
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting && !fired) {
          fired = true;
          var cards = band.querySelectorAll('.pr-review-band__card').length;
          trackEvent('review_band_view', {
            reviews_shown: cards,
            event_category: 'social_proof',
            event_action: 'visible'
          });
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(band);
  }

  // Shopify Customer Privacy → consent_changed (T17)
  function initConsentTracking() {
    function fire(payload) {
      var analytics = !!(payload && (payload.analyticsProcessingAllowed || payload.analytics_processing_allowed));
      var marketing = !!(payload && (payload.marketingAllowed || payload.marketing_allowed));
      trackEvent('consent_changed', {
        state: analytics ? 'granted' : 'denied',
        analytics: analytics,
        marketing: marketing,
        event_category: 'consent',
        event_action: 'updated'
      });
    }
    // Path A — DOM event (stable across Shopify versions)
    document.addEventListener('visitorConsentCollected', function(e) { fire(e && e.detail); });
    // Path B — Customer Privacy SDK subscribe (newer; optional)
    if (typeof window.Shopify !== 'undefined'
        && window.Shopify.customerPrivacy
        && typeof window.Shopify.customerPrivacy.subscribe === 'function') {
      window.Shopify.customerPrivacy.subscribe('visitorConsentCollected', fire);
    }
  }

  // Video Tracking
  function initVideoTracking() {
    // YouTube iframes
    document.querySelectorAll('iframe[src*="youtube"], iframe[src*="youtu.be"]').forEach(function(iframe) {
      trackEvent('video_impression', {
        video_provider: 'youtube',
        video_url: iframe.src
      });
    });

    // Vimeo iframes
    document.querySelectorAll('iframe[src*="vimeo"]').forEach(function(iframe) {
      trackEvent('video_impression', {
        video_provider: 'vimeo',
        video_url: iframe.src
      });
    });

    // HTML5 videos
    document.querySelectorAll('video').forEach(function(video) {
      if (video.dataset.gtmVideoTracked) return;
      video.dataset.gtmVideoTracked = 'true';

      video.addEventListener('play', function() {
        trackVideo('start', video.id, video.title || '', 'html5', video.currentTime, video.duration);
      });

      video.addEventListener('ended', function() {
        trackVideo('complete', video.id, video.title || '', 'html5', video.currentTime, video.duration);
      });

      // Track 25%, 50%, 75% progress
      var progressTracked = {};
      video.addEventListener('timeupdate', function() {
        var percent = Math.round((video.currentTime / video.duration) * 100);
        [25, 50, 75].forEach(function(milestone) {
          if (percent >= milestone && !progressTracked[milestone]) {
            progressTracked[milestone] = true;
            trackVideo('progress', video.id, video.title || '', 'html5', video.currentTime, video.duration);
          }
        });
      });
    });
  }

  // Popup/Modal Tracking
  function initPopupTracking() {
    // Watch for popup visibility
    var popupObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          var popup = mutation.target;
          if (popup.classList.contains('popup-modal') || popup.classList.contains('modal')) {
            var isVisible = popup.classList.contains('animate') || popup.classList.contains('active') || popup.classList.contains('open');
            var popupId = popup.id || 'unnamed_popup';
            var popupType = popup.classList.contains('promotion-popup') ? 'promotion' :
                           popup.classList.contains('newsletter') ? 'newsletter' : 'general';

            trackPopup(isVisible ? 'view' : 'close', popupId, popupType);
          }
        }
      });
    });

    document.querySelectorAll('.popup-modal, [class*="popup"], [class*="modal"]:not(.modal-dialog)').forEach(function(popup) {
      popupObserver.observe(popup, { attributes: true });
    });
  }

  // Back to Top Button Tracking
  function initBackToTopTracking() {
    document.querySelectorAll('[class*="back-to-top"], [class*="scroll-top"], #back-to-top').forEach(function(btn) {
      if (btn.dataset.gtmTracked) return;
      btn.dataset.gtmTracked = 'true';

      btn.addEventListener('click', function() {
        trackEvent('back_to_top_click', {
          scroll_position: window.scrollY,
          click_location: getElementLocation(this)
        });
      });
    });
  }

  // Product Card Click Tracking
  function initProductCardTracking() {
    document.querySelectorAll('.product-card, [class*="product-card"], .product-item').forEach(function(card) {
      if (card.dataset.gtmTracked) return;
      card.dataset.gtmTracked = 'true';

      card.addEventListener('click', function(e) {
        // Only track if clicking on the card itself or product link
        var link = e.target.closest('a');
        if (!link || !link.href.includes('/products/')) return;

        var productData = {
          id: card.dataset.productId || '',
          title: (card.querySelector('.product-card__name, .card__heading, h3, h4')?.textContent || '').trim(),
          price: (card.querySelector('.price, .money')?.textContent || '').replace(/[^\d.]/g, ''),
          vendor: card.dataset.vendor || 'Pane Relief'
        };

        var listName = card.closest('[data-section-name]')?.dataset.sectionName ||
                      card.closest('.collection')?.dataset.collectionHandle || 'product_list';
        var index = Array.from(card.parentElement.children).indexOf(card);

        trackSelectItem(productData, listName, index);
      });
    });
  }

  // Variant Selection Tracking
  function initVariantTracking() {
    document.querySelectorAll('[name^="option"], .product-form__input input, .swatch-input input').forEach(function(input) {
      if (input.dataset.gtmTracked) return;
      input.dataset.gtmTracked = 'true';

      input.addEventListener('change', function() {
        var productEl = this.closest('[data-product-handle]') || this.closest('.product');
        var productId = productEl?.dataset.productId || window.gtmProductData?.id || '';

        trackVariantChange(
          productId,
          this.value,
          this.dataset.optionValue || this.value,
          this.name.replace(/option\d?/, '').trim() || 'variant',
          this.dataset.optionValue || this.value
        );
      });
    });
  }

  // Wishlist Tracking
  function initWishlistTracking() {
    document.querySelectorAll('[class*="wishlist"], [data-wishlist]').forEach(function(btn) {
      if (btn.dataset.gtmWishlistTracked) return;
      btn.dataset.gtmWishlistTracked = 'true';

      btn.addEventListener('click', function() {
        var productCard = this.closest('.product-card, [data-product-id]') || this.closest('.product');
        var productData = {
          id: productCard?.dataset.productId || window.gtmProductData?.id || '',
          title: productCard?.querySelector('.product-card__name, .card__heading')?.textContent.trim() ||
                 window.gtmProductData?.title || '',
          price: window.gtmProductData?.price || 0
        };

        trackAddToWishlist(productData);
      });
    });
  }

  // Error Tracking
  //
  // Only report errors that originate in OUR OWN code. The window 'error' event
  // also fires for cross-origin "Script error." events (blank source — the browser
  // hides details for security) thrown by Shopify's own pixels/shop.app, third-party
  // GTM tags, browser extensions, and bots. Reporting those flooded js_error with
  // ~100k/quarter of uninformative noise (blank error_source) that buried any real
  // theme bug. Keep: theme asset files (/assets/…) and same-origin inline scripts.
  // Drop: blank sources, Shopify-platform paths, and any cross-origin script.
  function isOwnScriptError(src) {
    if (!src) return false; // cross-origin "Script error." — no detail, not ours
    try {
      var u = new URL(src, location.origin);
      var path = u.pathname;
      // Exclude Shopify-platform + third-party scripts FIRST (some live under /assets/ too).
      if (/\/cdn\/(shopifycloud|wpm)\/|\/web-pixels|\/\.well-known\/|checkout-web|monorail/.test(path)) return false;
      if (path.indexOf('/assets/') !== -1) return true; // theme asset bundle (shop domain or cdn.shopify.com)
      if (u.origin === location.origin) return true;    // same-origin inline / page script
      return false; // cross-origin, non-asset
    } catch (_e) { return src.indexOf('/assets/') !== -1; }
  }

  function initErrorTracking() {
    window.addEventListener('error', function(e) {
      if (!isOwnScriptError(e.filename)) return; // skip third-party / cross-origin noise
      trackEvent('js_error', {
        error_message: e.message || 'Unknown error',
        error_source: e.filename || '',
        error_line: e.lineno || 0,
        error_column: e.colno || 0,
        error_stack: e.error?.stack?.substring(0, 500) || ''
      });
    });

    document.addEventListener('error', function(e) {
      if (e.target.tagName === 'IMG') {
        trackEvent('image_error', {
          image_src: e.target.src || '',
          image_alt: e.target.alt || '',
          image_location: getElementLocation(e.target)
        });
      }
    }, true);
  }

  // Collection Filter/Sort Tracking
  function initCollectionTracking() {
    // Filter changes
    document.querySelectorAll('[name^="filter"], .facets__wrapper input, .collection-filters input').forEach(function(input) {
      if (input.dataset.gtmFilterTracked) return;
      input.dataset.gtmFilterTracked = 'true';

      input.addEventListener('change', function() {
        var collectionHandle = window.location.pathname.split('/collections/')[1]?.split('/')[0] || '';
        trackFilterApply(
          this.name.replace('filter.', ''),
          this.value,
          collectionHandle
        );
      });
    });

    // Sort changes
    document.querySelectorAll('[name="sort_by"], .collection-sort select').forEach(function(select) {
      if (select.dataset.gtmSortTracked) return;
      select.dataset.gtmSortTracked = 'true';

      select.addEventListener('change', function() {
        var collectionHandle = window.location.pathname.split('/collections/')[1]?.split('/')[0] || '';
        trackSortChange(this.value, collectionHandle);
      });
    });

    // Collection hero-strip chip clicks — delegated so re-renders re-bind
    document.addEventListener('click', function(e) {
      var chip = e.target.closest('.chs__chip');
      if (!chip) return;
      trackEvent('chip_click', {
        chip_label: chip.textContent.trim(),
        chip_slug: chip.dataset.chip || '',
        chip_collection: chip.dataset.chipCollection || '',
        chip_destination: chip.getAttribute('href') || '',
        event_category: 'collection_hero_strip',
        event_action: 'subcategory_chip'
      });
    });
  }

  // ════════════════════════════════════════════════════════════
  // UTILITY FUNCTIONS
  // ════════════════════════════════════════════════════════════
  function getElementLocation(el) {
    if (!el) return 'unknown';
    if (el.closest('header, .header')) return 'header';
    if (el.closest('footer, .footer')) return 'footer';
    if (el.closest('.hero, .banner, [class*="hero"], [class*="banner"]')) return 'hero';
    if (el.closest('.sidebar, aside')) return 'sidebar';
    if (el.closest('[class*="popup"], [class*="modal"]')) return 'popup';
    if (el.closest('.product, [data-product]')) return 'product_page';
    if (el.closest('.cart, [class*="cart"]')) return 'cart';
    if (el.closest('nav, .nav')) return 'navigation';
    if (el.closest('main, .main-content')) return 'main_content';
    return 'unknown';
  }

  function getFormName(form) {
    if (form.dataset.formName) return form.dataset.formName;
    if (form.id) {
      if (form.id.toLowerCase().includes('newsletter')) return 'Newsletter Signup';
      if (form.id.toLowerCase().includes('contact')) return 'Contact Form';
      return form.id.replace(/[-_]/g, ' ').replace(/form/gi, '').trim() || 'Form';
    }
    if (form.classList.contains('newsletter-form')) return 'Newsletter Signup';
    if (form.action.includes('contact')) return 'Contact Form';
    if (form.action.includes('customer')) return 'Newsletter Signup';
    return 'Unknown Form';
  }

  // ════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ════════════════════════════════════════════════════════════
  function init() {
    // Core tracking
    trackPageView();
    initScrollTracking();
    initTimeOnPageTracking();
    initCartTracking();
    initErrorTracking();
    initContactClickTracking();
    initOutboundTracking();
    initSocialTracking();
    initDownloadTracking();

    // T17 — consent listener registers at init (not domInit) so it catches
    // banner events fired before DOMContentLoaded.
    initConsentTracking();

    // DOM-dependent tracking
    var domInit = function() {
      initNavigationTracking();
      initCtaTracking();
      initFormTracking();
      initSearchTracking();
      initAccordionTracking();
      initFaqTracking();          // T17
      initVideoTracking();
      initPopupTracking();
      initBackToTopTracking();
      initReviewBandTracking();   // T17
      initProductCardTracking();
      initVariantTracking();
      initWishlistTracking();
      initCollectionTracking();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', domInit);
    } else {
      domInit();
    }
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Shopify section loads
  document.addEventListener('shopify:section:load', function() {
    initNavigationTracking();
    initCtaTracking();
    initFormTracking();
    initAccordionTracking();
    initProductCardTracking();
    initVariantTracking();
  });

})();
